import { BaseService, type ResponseMessage } from '@kiddocare-platform/portal-common/base'
import type { Beverage, DiaryType, Event, GrowthMeasure, Illness, Medicine, Reminder, Vaccination, VisitType } from '@kiddocare-platform/portal-common/family'
import { isCacheValid } from '../../../cache/entry.cache'
import { store } from '../../../cache/factory.cache'
import {
  addBeverages,
  addDiaryTypeKey,
  addDiaryTypesKey,
  addGrowthMeasures,
  addIllnesseses,
  addMedicines,
  addVaccinesKey,
  addVisitTypes,
  removeBeverages,
  removeDiaryTypeKey,
  removeDiaryTypesKey,
  removeGrowthMeasures,
  removeIllnesseses,
  removeMedicines,
  removeVaccinesKey,
  removeVisitTypes,
} from '../../../cache/slices/family'

export class FamilyDiaryService extends BaseService {
  public async GetTypes(idAccountPatient: bigint): Promise<ResponseMessage<Array<DiaryType>>> {
    try {
      const key = idAccountPatient.toString()
      const cachedEntry = store.getState().family.diaryTypes?.[key]
      if (isCacheValid(cachedEntry)) {
        return cachedEntry.value
      }
      if (cachedEntry) {
        store.dispatch(removeDiaryTypesKey(key))
      }
      const response = await this.AxiosInstance.get(`parent/diary/types/list/${idAccountPatient}`)
      store.dispatch(addDiaryTypesKey({ key, value: response.data }))
      return response.data
    } catch (error: any) {
      this.log.error('GetTypes', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetType(code: string): Promise<ResponseMessage<DiaryType>> {
    try {
      const key = code
      const cachedEntry = store.getState().family.diaryType?.[key]
      if (isCacheValid(cachedEntry)) {
        return cachedEntry.value
      }
      if (cachedEntry) {
        store.dispatch(removeDiaryTypeKey(key))
      }
      const response = await this.AxiosInstance.get(`/parent/diary/types/getType/${code}`)
      store.dispatch(addDiaryTypeKey({ key, value: response.data }))
      return response.data
    } catch (error: any) {
      this.log.error('GetType', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async CreateEvent(
    idAccountPatient: bigint,
    params: {
      idType: bigint
      startedAt: Date
      code?: string
      status?: string
      endedAt?: Date
      comment?: string
      data?: any
    },
  ): Promise<ResponseMessage<Event>> {
    try {
      const response = await this.AxiosInstance.post(
        `/parent/diary/events/create/${idAccountPatient}`,
        params,
      )
      store.dispatch(removeGrowthMeasures(idAccountPatient.toString()))
      return response.data
    } catch (error: any) {
      this.log.error('CreateEvent', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetTimeline(
    idAccountPatient: bigint,
    params: {
      startedAt: string
      endedAt: string
      code?: string
    },
  ): Promise<ResponseMessage<Array<Event>>> {
    try {
      const response = await this.AxiosInstance.post(
        `/parent/diary/events/getTimeline/${idAccountPatient}`,
        params,
      )
      return response.data
    } catch (error: any) {
      this.log.error('GetTimeline', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetInProgress(
    idAccountPatient: bigint,
    code?: string,
  ): Promise<ResponseMessage<Array<Event>>> {
    try {
      const response = await this.AxiosInstance.post(
        `/parent/diary/events/getInProgress/${idAccountPatient}`,
        { code },
      )
      return response.data
    } catch (error: any) {
      this.log.error('GetInProgress', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetLatestGrowthMeasure(
    idAccountPatient: bigint,
  ): Promise<ResponseMessage<GrowthMeasure>> {
    try {
      const key = idAccountPatient.toString()
      const cachedEntry = store.getState().family.growthMeasures?.[key]
      if (isCacheValid(cachedEntry)) {
        return cachedEntry.value
      }
      if (cachedEntry) {
        store.dispatch(removeGrowthMeasures(key))
      }
      const response = await this.AxiosInstance.get(
        `/parent/diary/events/getLatestGrowthMeasure/${idAccountPatient}`,
      )
      store.dispatch(addGrowthMeasures({ key, value: response.data }))
      return response.data
    } catch (error: any) {
      this.log.error('GetLatestGrowthMeasure', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetEventById(
    idAccountPatient: bigint,
    eventId: bigint,
  ): Promise<ResponseMessage<Event>> {
    try {
      const response = await this.AxiosInstance.get(
        `/parent/diary/events/getById/${idAccountPatient}/${eventId}`,
      )
      return response.data
    } catch (error: any) {
      this.log.error('GetEventById', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async UpdateEvent(
    idAccountPatient: bigint,
    eventId: bigint,
    params: {
      startedAt: Date
      endedAt?: Date
      comment?: string
      data?: any
    },
  ): Promise<ResponseMessage<unknown>> {
    try {
      const response = await this.AxiosInstance.post(
        `/parent/diary/events/update/${idAccountPatient}/${eventId}`,
        params,
      )
      store.dispatch(removeGrowthMeasures(idAccountPatient.toString()))
      return response.data
    } catch (error: any) {
      this.log.error('UpdateEvent', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async StopEvent(
    idAccountPatient: bigint,
    eventId: bigint,
    params: {
      endedAt: Date
    },
  ): Promise<ResponseMessage<unknown>> {
    try {
      const response = await this.AxiosInstance.post(
        `/parent/diary/events/stop/${idAccountPatient}/${eventId}`,
        params,
      )
      return response.data
    } catch (error: any) {
      this.log.error('StopEvent', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async DeleteEvent(
    idAccountPatient: bigint,
    eventId: bigint,
  ): Promise<ResponseMessage<unknown>> {
    try {
      const response = await this.AxiosInstance.delete(
        `/parent/diary/events/delete/${idAccountPatient}/${eventId}`,
      )
      store.dispatch(removeGrowthMeasures(idAccountPatient.toString()))
      return response.data
    } catch (error: any) {
      this.log.error('DeleteEvent', error.stack, JSON.stringify(error))
      throw error
    }
  }

  // public async GetPlanningsList(
  //   idAccountPatient: bigint,
  // ): Promise<ResponseMessage<Array<Planning>>> {
  //   try {
  //     const response = await this.AxiosInstance.get(
  //       `/parent/diary/plannings/getList/${idAccountPatient}`,
  //     )
  //     return response.data
  //   } catch (error: any) {
  //     this.log.error('GetPlanningsList', error.stack, JSON.stringify(error))
  //     throw error
  //   }
  // }

  public async GetLastEvent(
    idAccountPatient: bigint,
    code: string,
  ): Promise<ResponseMessage<Event>> {
    try {
      const response = await this.AxiosInstance.get(
        `/parent/diary/events/getLastEvent/${idAccountPatient}/${code}`,
      )
      return response.data
    } catch (error: any) {
      this.log.error('GetLastEvent', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async SearchCount(
    idAccountPatient: bigint,
    params: {
      startDate: string
      endDate: string
      code?: string
    },
  ): Promise<ResponseMessage<Array<Record<string, number>>>> {
    try {
      const response = await this.AxiosInstance.post(
        `/parent/diary/events/searchCount/${idAccountPatient}`,
        params,
      )
      return response.data
    } catch (error: any) {
      this.log.error('SearchCount', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetListVisitTypes(): Promise<ResponseMessage<Array<VisitType>>> {
    try {
      const cachedEntry = store.getState().family.visitTypes
      if (isCacheValid(cachedEntry)) {
        return cachedEntry.value
      }
      if (cachedEntry) {
        store.dispatch(removeVisitTypes())
      }
      const response = await this.AxiosInstance.get(`parent/diary/visitTypes/list`)
      store.dispatch(addVisitTypes(response.data))
      return response.data
    } catch (error: any) {
      this.log.error('GetListVisitTypes', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetListVaccines(
    idAccountPatient: bigint,
  ): Promise<ResponseMessage<Array<Vaccination>>> {
    try {
      const key = idAccountPatient.toString()
      const cachedEntry = store.getState().family.vaccines?.[key]
      if (isCacheValid(cachedEntry)) {
        return cachedEntry.value
      }
      if (cachedEntry) {
        store.dispatch(removeVaccinesKey(key))
      }
      const response = await this.AxiosInstance.get(
        `parent/diary/vaccines/list/${idAccountPatient}`,
      )
      store.dispatch(addVaccinesKey({ key, value: response.data }))
      return response.data
    } catch (error: any) {
      this.log.error('GetListVaccines', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetListIllnesses(): Promise<ResponseMessage<Array<Illness>>> {
    try {
      const cachedEntry = store.getState().family.illnesses
      if (isCacheValid(cachedEntry)) {
        return cachedEntry.value
      }
      if (cachedEntry) {
        store.dispatch(removeIllnesseses())
      }
      const response = await this.AxiosInstance.get(`parent/diary/illnesses/list`)
      store.dispatch(addIllnesseses(response.data))
      return response.data
    } catch (error: any) {
      this.log.error('GetListIllnesses', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetListMedicines(): Promise<ResponseMessage<Array<Medicine>>> {
    try {
      const cachedEntry = store.getState().family.medicines
      if (isCacheValid(cachedEntry)) {
        return cachedEntry.value
      }
      if (cachedEntry) {
        store.dispatch(removeMedicines())
      }
      const response = await this.AxiosInstance.get(`parent/diary/medicines/list`)
      store.dispatch(addMedicines(response.data))
      return response.data
    } catch (error: any) {
      this.log.error('GetListMedicines', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async GetListBeverages(): Promise<ResponseMessage<Array<Beverage>>> {
    try {
      const cachedEntry = store.getState().family.beverages
      if (isCacheValid(cachedEntry)) {
        return cachedEntry.value
      }
      if (cachedEntry) {
        store.dispatch(removeBeverages())
      }
      const response = await this.AxiosInstance.get(`parent/diary/beverages/list`)
      store.dispatch(addBeverages(response.data))
      return response.data
    } catch (error: any) {
      this.log.error('GetListBeverages', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async CreatePreferences(types: Array<string>): Promise<ResponseMessage<unknown>> {
    try {
      const response = await this.AxiosInstance.post(`parent/diary/typePreferences/create`, {
        types,
      })
      const diaryTypesCache = store.getState().family.diaryTypes
      for (const key of Object.keys(diaryTypesCache)) {
        store.dispatch(removeDiaryTypesKey(key))
      }
      const diaryTypeCache = store.getState().family.diaryType
      for (const key of Object.keys(diaryTypeCache)) {
        store.dispatch(removeDiaryTypeKey(key))
      }
      return response.data
    } catch (error: any) {
      this.log.error('CreatePreferences', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async ListReminders(idAccountPatient: bigint): Promise<ResponseMessage<Array<Reminder>>> {
    try {
      const response = await this.AxiosInstance.get(
        `parent/diary/reminders/list/${idAccountPatient}`,
      )
      return response.data
    } catch (error: any) {
      this.log.error('ListReminders', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async CreateReminder(
    idAccountPatient: bigint,
    params: {
      code: string
      name: string
      expression: string
      startAt: Date
      data?: any
    },
  ): Promise<ResponseMessage<unknown>> {
    try {
      const response = await this.AxiosInstance.post(
        `/parent/diary/reminders/create/${idAccountPatient}`,
        params,
      )
      return response.data
    } catch (error: any) {
      this.log.error('CreateReminder', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async DeleteReminder(
    idAccountPatient: bigint,
    idReminder: bigint,
  ): Promise<ResponseMessage<unknown>> {
    try {
      const response = await this.AxiosInstance.delete(
        `/parent/diary/reminders/delete/${idAccountPatient}/${idReminder}`,
      )
      return response.data
    } catch (error: any) {
      this.log.error('DeleteReminder', error.stack, JSON.stringify(error))
      throw error
    }
  }

  public async UpdateReminder(
    idAccountPatient: bigint,
    idReminder: bigint,
    params: {
      code: string
      name: string
      expression: string
      startAt: Date
      data?: any
    },
  ): Promise<ResponseMessage<unknown>> {
    try {
      const response = await this.AxiosInstance.post(
        `/parent/diary/reminders/update/${idAccountPatient}/${idReminder}`,
        params,
      )
      return response.data
    } catch (error: any) {
      this.log.error('UpdateReminder', error.stack, JSON.stringify(error))
      throw error
    }
  }
}
